import { AIR } from '@vm/shared';
import { M } from '../palette';
import { brushAt, MapBuilder, speckle, stripes, symPoint, yawToward, type Brush } from './builder';
import { stairKeepClear, bar, doorsFor, freeGround, G, house, lampPost, palmTree, parasol, pieces, Region, scatter, TRIM, type Box, type Footprint, type Piece, ceilingStrip, regionPlay } from './kit';
import { emptyMeta, type MapDef, type MapMeta } from './types';

/**
 * Sunset Cruiser: a cruise ferry at sunset, in the layout of a famous yacht arena map (see docs/maps/reference-maps.md): a long, narrow deck with
 * a cabin block fore and aft of an open central deck, a bar and hot tub on the north side, a snack kiosk on the south side, lifeboats at the
 * bow and a sun lounge at the stern. The plan is the reference's (80 x 17 m, the longest and narrowest map so far); everything you see is ours:
 * a white hull with a gold stripe over a navy waterline, teak decks, teal and orange trim, a red-and-white funnel, a radar mast, parasols and
 * a sun deck you can climb to. The sea is solid hull wall, a 1.5 m bulwark (a jump reaches 1.3 m) and open water beyond.
 * Not mirrored: both ends are matched by hand. Teams start at the stern and the bow.
 */
const SX = 1024;
const SY = 192;
const SZ = 384;
const OX = 112; // play-area origin (voxels)
const OZ = 105;
const px = (x: number): number => OX + Math.round(x * 10);
const pz = (z: number): number => OZ + Math.round(z * 10);
const wx = (x: number): number => OX / 10 + x; // play metres -> world metres
const wz = (z: number): number => OZ / 10 + z;
const SEABED = 5; // first voxel above the sea floor
const SEA_TOP = 8; // first air voxel above the water (0.8 m)
const BULWARK = 11; // solid bulwark above the deck (1.1 m)
const RAIL_TOP = 36; // top of the railing above the deck (3.6 m): the tallest thing a player can stand on beside it is 1.7 m (a lifeboat canopy), and a jump adds 1.3 m
const BAND = 1.6; // metres of hull wall outside the deck outline

const OUTLINE: [number, number][] = [
  [3.6, 7.2], [6.1, 4.8], [13.4, 2.5], [15.1, 3.5], [17.6, 3.2], [18.3, 2.0], [30.7, 2.0], [31.0, 0.0], [51.2, 0.0], [51.4, 1.9], [63.8, 1.9], [68.9, 3.1],
  [74.9, 5.8], [80.0, 9.7], [77.0, 12.2], [72.8, 14.6], [68.7, 16.3], [63.9, 17.4], [18.4, 17.4], [17.7, 16.3], [15.3, 15.8], [13.6, 16.8], [9.6, 15.8],
  [5.1, 13.8], [3.0, 11.8], [2.4, 9.7], [3.0, 7.9],
];
const region = new Region(OUTLINE);

// Palette ---------------------------------------------------------------------------------------
const HULL = M('hull_white');
const TEAL = M('paint_teal');
const ORANGE = M('paint_orange');
const GOLD = M('gold');
const DECK_A = M('planks_3');
const DECK_B = M('planks_1');
const LIGHTS = [M('neon_orange'), M('neon_yellow'), M('neon_pink'), M('neon_cyan')];
const CUSHION = [M('cloth_blue'), M('paint_teal'), M('cloth_red'), M('cloth_white')];

// Buildings: footprints in play metres ----------------------------------------------------------------------------
type Kind = 'lounge' | 'sundeck' | 'kiosk';
interface Bld extends Footprint {
  name: string;
  floors: number;
  kind: Kind;
  roofAccess?: boolean;
}
const BUILDINGS: Bld[] = [
  { name: 'aftcabin', x0: 23.8, z0: 3.7, x1: 35.7, z1: 15.5, floors: 2, kind: 'lounge' },
  { name: 'forecabin', x0: 48.7, z0: 3.8, x1: 61.3, z1: 15.5, floors: 2, kind: 'sundeck', roofAccess: true },
  { name: 'kiosk', x0: 37.6, z0: 13.8, x1: 45.4, z1: 17.2, floors: 1, kind: 'kiosk' },
];

const place = (x: number, z: number, r: number, fn: () => void): void => {
  if (region.inside(x, z) && region.edgeDistance(x, z) > r) fn();
};

function furnish(b: MapBuilder, bd: Bld, box: Box, seed: number): void {
  const boxOf = box;
  for (let f = 0; f < bd.floors; f++) {
    const y = G + 30 * f;
    const room = { x0: box.x0 + 2, z0: box.z0 + 2, x1: box.x1 - 2, z1: box.z1 - 2, y };
    const avoid = stairKeepClear(boxOf, f);
    const lots: Piece[] = [];
    const up = f > 0;
    if (bd.kind === 'kiosk') lots.push(pieces.counter(b, y, 50, 7), pieces.shelfX(b, y, 26, 22), pieces.crate(b, y, 8), pieces.barrel(b, y, M('paint_orange')));
    else if (!up) lots.push(pieces.bar(b, y, 44, 8), pieces.sofa(b, y, M('cloth_blue')), pieces.sofa(b, y, M('paint_teal')), pieces.table(b, y, 20, 12), pieces.table(b, y, 20, 12), pieces.shelfX(b, y, 24, 22), pieces.sofa(b, y, M('cloth_red')));
    else lots.push(pieces.bed(b, y, M('cloth_blue')), pieces.bed(b, y, M('cloth_red')), pieces.bed(b, y, M('cloth_yellow')), pieces.shelfX(b, y, 22, 20), pieces.table(b, y, 14, 10), pieces.sofa(b, y, M('cloth_white')));
    scatter(b, room, lots, seed + f * 3, avoid);
    // Ceiling lights down the middle of the room, away from the stairs' head room.
    ceilingStrip(b, room, G + 27 + 30 * f + (f === bd.floors - 1 ? 1 : 0), LIGHTS, avoid, 26, f);
  }
}

/** Daybed: a white sun lounger with a coloured cushion, 28 x 10 x 7 voxels, long side along x (or z). */
function daybed(b: MapBuilder, x: number, z: number, color: number, alongX = true): void {
  const [lx, lz] = alongX ? [28, 10] : [10, 28];
  b.box(x, G, z, x + lx, G + 4, z + lz, M('paint_white'));
  b.box(x + 1, G + 4, z + 1, x + lx - 1, G + 7, z + lz - 1, color);
  if (alongX) b.box(x, G + 4, z, x + 3, G + 11, z + lz, M('paint_white'));
  else b.box(x, G + 4, z, x + lx, G + 11, z + 3, M('paint_white'));
}

/** Lifeboat on its cradle: a white boat with an orange stripe, an open cockpit and a canopy, 56 x 33 x 20 voxels (cover). */
function lifeboat(b: MapBuilder, x: number, z: number, alongX: boolean): void {
  const [lx, lz] = alongX ? [56, 33] : [33, 56];
  b.box(x, G, z, x + lx, G + 3, z + lz, M('steel_dark_1'));
  b.box(x + 1, G + 3, z + 1, x + lx - 1, G + 15, z + lz - 1, HULL);
  b.box(x + 1, G + 9, z + 1, x + lx - 1, G + 11, z + lz - 1, ORANGE);
  const inset = 5;
  b.clear(x + inset, G + 13, z + inset, x + lx - inset, G + 15, z + lz - inset);
  b.box(x + inset + 1, G + 15, z + inset + 1, x + lx - inset - 1, G + 17, z + lz - inset - 1, ORANGE);
}

function build(b: MapBuilder): MapMeta {
  // The sea, the hull and the deck ------------------------------------------------------------------------------------------
  b.ground(SEABED, M('sand_2'), M('sand_1'), M('rock_sand'));
  b.box(0, SEABED, 0, SX, SEA_TOP, SZ, M('deep_water'));
  const bad: string[] = [];
  for (const bd of BUILDINGS)
    for (const [x, z] of [[bd.x0, bd.z0], [bd.x1, bd.z0], [bd.x0, bd.z1], [bd.x1, bd.z1]])
      if (!region.inside(x, z) || region.edgeDistance(x, z) < 0.15) bad.push(`${bd.name} (${x}, ${z})`);
  if (bad.length) throw new Error(`sunset_cruiser: corners outside or on the deck outline: ${bad.join('; ')}`);
  const deck: Brush = (x, _y, z) => (((z >> 2) + (x >> 6)) % 2 === 0 ? DECK_A : DECK_B);
  const side: Brush = (_x, y) => (y < 14 ? M('stucco_navy') : y >= G - 5 && y < G - 3 ? GOLD : y >= G + BULWARK - 1 ? M('chrome') : HULL);
  const railMat = M('paint_white');
  for (let Z = pz(-2.4); Z < pz(19.8); Z++) {
    for (let X = px(0); X < px(81.6); X++) {
      const xm = (X - OX + 0.5) / 10;
      const zm = (Z - OZ + 0.5) / 10;
      if (region.inside(xm, zm)) {
        b.box(X, SEABED, Z, X + 1, G - 1, Z + 1, M('hull_gray'));
        b.set(X, G - 1, Z, brushAt(deck, X, G - 1, Z));
      } else if (region.edgeDistance(xm, zm) <= BAND) {
        b.box(X, SEABED, Z, X + 1, G + BULWARK, Z + 1, side);
        // The railing stands on the inner skin of the bulwark: posts every 4 voxels and a top rail.
        if (region.edgeDistance(xm, zm) <= 0.3) {
          if ((X + Z) % 4 === 0) b.box(X, G + BULWARK, Z, X + 1, G + RAIL_TOP, Z + 1, railMat);
          b.box(X, G + RAIL_TOP - 1, Z, X + 1, G + RAIL_TOP, Z + 1, GOLD);
        }
      }
    }
  }
  // Cabins and the kiosk ------------------------------------------------------------------------------------------------------
  const boxes = new Map<string, Box>();
  for (const bd of BUILDINGS) {
    const doors = doorsFor(bd, BUILDINGS, region, 'sunset_cruiser');
    const box = house(b, {
      x0: px(bd.x0), z0: pz(bd.z0), x1: px(bd.x1), z1: pz(bd.z1), floors: bd.floors, wall: HULL, roof: M('paint_white'), roofStyle: 'flat', doors,
      post: TEAL, trim: TEAL, plinth: M('steel_dark_1'), floor: M('planks_2'), windows: { w: 16, h: 12, sill: 9, spacing: 26 }, parapet: 4,
      roofAccess: bd.roofAccess, railing: bd.roofAccess ? 24 : undefined,
    });
    boxes.set(bd.name, box);
  }
  let seed = 2;
  for (const bd of BUILDINGS) furnish(b, bd, boxes.get(bd.name)!, (seed += 7));
  // Orange band round both cabins at the floor line, and teal signs on the end walls.
  for (const bd of BUILDINGS) {
    const bx = boxes.get(bd.name)!;
    b.walls(bx.x0 - 1, G + 28, bx.z0 - 1, bx.x1 + 1, G + 30, bx.z1 + 1, 1, ORANGE);
  }
  // The funnel over the aft cabin and the radar mast over the fore cabin's sun deck.
  {
    const bx = boxes.get('aftcabin')!;
    const cx = (bx.x0 + bx.x1) >> 1;
    const cz = (bx.z0 + bx.z1) >> 1;
    const roof = bx.top;
    b.cylinder(cx, cz, roof, roof + 32, 11, M('paint_red'));
    b.cylinder(cx, cz, roof + 32, roof + 40, 11, M('paint_white'));
    b.cylinder(cx, cz, roof + 40, roof + 48, 11, M('paint_black'));
    b.cylinder(cx, cz, roof + 18, roof + 22, 11.5, GOLD);
  }
  {
    const bx = boxes.get('forecabin')!;
    const mx = ((bx.x0 + bx.x1) >> 1) + 20;
    const mz = (bx.z0 + bx.z1) >> 1;
    b.box(mx, bx.top, mz, mx + 2, bx.top + 44, mz + 2, M('chrome'));
    b.box(mx - 7, bx.top + 38, mz, mx + 9, bx.top + 40, mz + 2, M('chrome'));
    b.dome(mx + 1, bx.top + 44, mz + 1, 7, M('paint_white'));
    b.box(mx - 3, bx.top + 2, mz - 3, mx + 5, bx.top + 3, mz + 5, M('steel_dark_1'));
  }
  // Sun deck loungers on the fore cabin's roof, clear of the stair opening.
  {
    const bx = boxes.get('forecabin')!;
    const y = bx.top + 2;
    for (const [dx, dz, c] of [[62, 8, 0], [62, 24, 1], [94, 8, 2]] as [number, number, number][]) {
      const x = bx.x0 + dx;
      const z = bx.z0 + dz;
      b.box(x, y, z, x + 28, y + 4, z + 10, M('paint_white'));
      b.box(x + 1, y + 4, z + 1, x + 27, y + 7, z + 9, CUSHION[c]);
    }
  }

  // The north side: bar, hot tub and pergola ---------------------------------------------------------------------------
  {
    const x0 = px(33.4);
    const x1 = px(48.6);
    // Bar counter against the bulwark with stools and a bottle shelf behind it.
    b.box(x0, G, pz(0.45), x1, G + 10, pz(1.25), M('planks_dark_1'));
    b.box(x0, G + 10, pz(0.45), x1, G + 11, pz(1.25), M('planks_3'));
    for (let x = x0 + 4; x < x1 - 4; x += 4) b.box(x, G + 11, pz(0.5), x + 1, G + 15, pz(0.5) + 1, [M('paint_green'), ORANGE, M('paint_red')][((x - x0) >> 2) % 3]);
    for (let x = x0 + 5; x < x1 - 4; x += 9) {
      b.box(x, G, pz(1.9), x + 1, G + 7, pz(1.9) + 1, M('steel_dark_1'));
      b.box(x - 1, G + 7, pz(1.9) - 1, x + 2, G + 8, pz(1.9) + 2, TEAL);
    }
    // Pergola: gold posts with a striped canopy over the bar.
    for (const x of [x0, x1 - 1]) b.box(x, G, pz(2.3), x + 1, G + 30, pz(2.3) + 1, GOLD);
    b.box(x0 - 2, G + 30, pz(0.2), x1 + 2, G + 31, pz(2.5), stripes(TEAL, M('paint_white'), 4, 'x'));
    // Hot tub: a round basin with water and a white arch behind it.
    const cx = px(41.0);
    const cz = pz(1.3);
    b.cylinder(cx, cz, G, G + 7, 8, M('paint_white'));
    b.cylinder(cx, cz, G + 4, G + 7, 6, AIR);
    b.cylinder(cx, cz, G + 3, G + 6, 6, M('water'));
  }
  // The south side: the kiosk's terrace with parasols, a daybed row and potted palms ----------------------------------------------------
  for (const [x, z, c] of [[35.2, 13.4, 0], [48.0, 14.0, 1]] as [number, number, number][]) place(x, z, 1.4, () => parasol(b, px(x), pz(z), [ORANGE, TEAL][c]));
  // Central deck: daybeds as low cover, a long table, lamps.
  place(41.5, 9.6, 1.5, () => {
    b.box(px(39.1), G, pz(8.1), px(44.2), G + 9, pz(10.9), M('paint_white'));
    b.box(px(39.1) + 1, G + 9, pz(8.1) + 1, px(44.2) - 1, G + 10, pz(10.9) - 1, TEAL);
  });
  for (const [x, z, c, ax] of [[37.2, 5.2, 0, true], [45.2, 5.2, 1, true], [37.2, 13.2, 2, true], [46.0, 12.2, 3, true]] as [number, number, number, boolean][])
    place(x + 1.4, z + 0.5, 1.2, () => {
      if (freeGround(b, px(x), pz(z), px(x) + 28, pz(z) + 10)) daybed(b, px(x), pz(z), CUSHION[c], ax);
    });

  // The bow: lifeboats on cradles, an anchor winch and bollards --------------------------------------------------------------------
  place(66.8, 15.0, 0.6, () => lifeboat(b, px(64.2), pz(13.0), true));
  place(66.8, 4.6, 0.6, () => lifeboat(b, px(64.2), pz(2.9) + 4, true));
  place(72.0, 9.7, 1.0, () => {
    const cx = px(72.0);
    const cz = pz(9.7);
    b.cylinder(cx, cz, G, G + 8, 4, M('steel_dark_1'));
    b.cylinder(cx, cz, G + 8, G + 10, 5, GOLD);
    b.box(cx - 8, G, cz - 2, cx - 4, G + 6, cz + 3, M('steel_dark_1'));
  });
  for (const [x, z] of [[76.4, 8.6], [76.4, 10.8], [66.6, 9.8]] as [number, number][])
    place(x, z, 0.7, () => {
      b.cylinder(px(x), pz(z), G, G + 7, 2.4, M('paint_black'));
      b.cylinder(px(x), pz(z), G + 7, G + 9, 3.2, ORANGE);
    });
  // Flag pole on the bow with a team-neutral pennant.
  b.box(px(78.0), G, pz(9.7), px(78.0) + 2, G + 50, pz(9.7) + 2, M('chrome'));
  b.box(px(78.0) - 18, G + 40, pz(9.7), px(78.0), G + 48, pz(9.7) + 1, stripes(ORANGE, M('paint_white'), 4, 'x'));

  // The stern: a sun lounge under a canopy, curved sofas, parasols, palms in planters -----------------------------------------------------
  place(10.0, 9.7, 2.0, () => {
    const cx = px(10.0);
    const cz = pz(9.7);
    for (const [dx, dz] of [[-25, -25], [25, -25], [-25, 25], [25, 25]]) b.box(cx + dx, G, cz + dz, cx + dx + 1, G + 32, cz + dz + 1, GOLD);
    b.box(cx - 28, G + 32, cz - 28, cx + 28, G + 33, cz + 28, stripes(TEAL, M('paint_white'), 4, 'z'));
    b.box(cx - 8, G, cz - 8, cx + 8, G + 6, cz + 8, M('paint_white'));
    b.box(cx - 7, G + 6, cz - 7, cx + 7, G + 7, cz + 7, TEAL);
  });
  for (const [x, z, c, ax] of [[5.6, 5.6, 0, false], [5.6, 12.0, 1, false], [13.6, 6.0, 2, true], [13.6, 12.6, 3, true]] as [number, number, number, boolean][])
    place(x + 0.5, z + 1.4, 1.0, () => {
      if (freeGround(b, px(x), pz(z), px(x) + (ax ? 28 : 10), pz(z) + (ax ? 10 : 28))) daybed(b, px(x), pz(z), CUSHION[c], ax);
    });
  for (const [x, z] of [[8.2, 4.4], [8.2, 14.2], [16.4, 4.6], [16.8, 15.0]] as [number, number][])
    place(x, z, 1.4, () => {
      b.box(px(x) - 4, G, pz(z) - 4, px(x) + 5, G + 5, pz(z) + 5, M('brick_red_3'));
      palmTree(b, px(x), pz(z), 36, 'e');
    });
  place(19.4, 9.7, 1.0, () => {
    // The stern's wedge benches by the aft cabin: two corner seats.
    for (const dz of [-42, 36]) b.box(px(20.2), G, pz(9.7) + dz, px(22.6), G + 5, pz(9.7) + dz + 8, M('paint_white'));
  });
  for (const [x, z, c] of [[22.4, 2.8, 0], [22.4, 16.4, 1], [63.0, 2.8, 2], [63.0, 16.6, 3], [41.0, 3.4, 0], [41.0, 16.2, 1]] as [number, number, number][])
    place(x, z, 0.8, () => lampPost(b, px(x), pz(z), LIGHTS[c]));

  // Metadata ----------------------------------------------------------------------------------------------------------------------------
  const gy = G / 10;
  const meta = emptyMeta(SEA_TOP / 10, wx(40.0), wz(9.7));
  const mid: [number, number] = [wx(41), wz(9.7)];
  const mirror = (x: number, z: number): [number, number] => symPoint('mirrorX', 80.0, 19.4, x, z);
  const t0: [number, number][] = [[5.2, 8.0], [5.2, 9.7], [5.2, 11.4], [7.0, 8.0], [7.0, 11.4], [6.0, 6.4], [6.0, 13.0], [9.0, 9.7]];
  for (const [x, z] of t0) {
    meta.spawns.push({ team: 0, x: wx(x), y: gy, z: wz(z), yaw: yawToward(wx(x), wz(z), mid[0], mid[1]) });
    const [mx, mz] = mirror(x, z);
    meta.spawns.push({ team: 1, x: wx(mx), y: gy, z: wz(mz), yaw: yawToward(wx(mx), wz(mz), mid[0], mid[1]) });
  }
  const ffa: [number, number][] = [
    [16.0, 9.7], [21.0, 9.7], [27.0, 2.8], [27.0, 16.5], [40.0, 5.2], [43.0, 4.6], [38.0, 12.0], [46.0, 11.0], [44.5, 16.0], [55.0, 2.8], [55.0, 16.6],
    [65.0, 9.8], [70.0, 5.5], [70.0, 13.6], [62.5, 9.7], [31.0, 9.7],
  ];
  for (const [x, z] of ffa) meta.spawns.push({ team: -1, x: wx(x), y: gy, z: wz(z), yaw: yawToward(wx(x), wz(z), mid[0], mid[1]) });
  meta.points.push({ id: 'A', name: 'Stern Lounge', x: wx(14.0), y: gy, z: wz(9.7), r: 3.2 });
  meta.points.push({ id: 'B', name: 'Central Deck', x: wx(41.5), y: gy, z: wz(6.4), r: 3.0 });
  meta.points.push({ id: 'C', name: 'Bow Deck', x: wx(70.0), y: gy, z: wz(9.7), r: 3.2 });
  meta.flags.push({ team: 0, x: wx(13.0), y: gy, z: wz(9.7) });
  meta.flags.push({ team: 1, x: wx(80.0 - 13.0 + 6.0), y: gy, z: wz(9.7) });
  const picks: [string, number, number][] = [
    ['health', 20.0, 5.0], ['health', 20.0, 14.6], ['health', 41.5, 13.0], ['health', 62.0, 9.7], ['armor', 29.7, 9.6], ['armor', 55.0, 9.6], ['health', 46.0, 5.2],
    ['health', 37.0, 5.2],
  ];
  for (const [kind, x, z] of picks) meta.pickups.push({ kind: kind as 'health' | 'armor', x: wx(x), y: b.standAt(wx(x), wz(z), 4.5), z: wz(z) });

  // Keep the voxels around every spawn clear and make sure there is floor under it.
  for (const s of meta.spawns) {
    const x = Math.floor(s.x * 10);
    const z = Math.floor(s.z * 10);
    const y = Math.round(s.y * 10);
    b.clear(x - 3, y, z - 3, x + 4, y + 18, z + 4);
    if (b.w.get(x, y - 1, z) === AIR) b.set(x, y - 1, z, M('stone_1'));
  }
  void speckle;
  void TRIM;
  // Nothing that moves can leave the boundary: an invisible wall at every height along it (the boundary wall is the visible one).
  regionPlay(b, region, OX, OZ, BUILDINGS);
  return meta;
}

export const sunsetCruiser: MapDef = {
  id: 'sunset_cruiser',
  name: 'Sunset Cruiser',
  description: 'A cruise ferry at sunset: a long teak deck, two cabins, a bar and a sun deck. Nobody overboard.',
  size: [SX, SY, SZ],
  modes: ['tdm', 'ffa', 'dom', 'ctf', 'sandbox', 'practice'],
  players: [4, 12],
  seed: 5104,
  version: 2,
  theme: {
    skyTop: 0x4a6cc8,
    skyHorizon: 0xffb890,
    fog: 0xffd0a8,
    fogNear: 55,
    fogFar: 190,
    sun: [0.75, 0.38, 0.25],
    sunColor: 0xffc890,
    sunIntensity: 1.45,
    ambient: 0xffe8d8,
    ambientIntensity: 1.0,
    outside: 0x3368c8,
    music: 'town',
  },
  build,
};
